import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Typography,
  Tabs,
  Tab,
  Box,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { Add } from "@mui/icons-material";
import Modal from "../../components/Modal/Modal";
import SwipeableViews from "react-swipeable-views";
import CreateCustomWidget from "./CreateCustomWidget";
import { filesPreloading } from "../../services/fileUploadService";

const customWidgetDefaultLayout = {
  w: 4,
  h: 4,
  minW: 4,
  minH: 4,
  x: 0,
  y: 10000000
};

const useStyles = makeStyles(theme => ({
  tabs: {
    marginBottom: theme.spacing(3)
  },
  saveButton: {
    marginLeft: 24
  },
  divider: {
    width: 1,
    backgroundColor: theme.palette.secondary[300],
    margin: theme.spacing(3, 1, 0, 1),
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  }
}));

const WidgetsModalTabs = [
  { key: 0, label: "System Widgets" },
  { key: 1, label: "Custom Widgets" }
];

const TabContainer = ({ children }) => {
  return (
    <Box display="flex" flexDirection="column" pl={0.25}>
      <div>{children}</div>
    </Box>
  );
};

const WidgetsCheckboxList = ({
  widgets,
  currentUser,
  onWidgetSelectedChange
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const widgetSubtitle = widget => {
    if (widget.isPrivate) return t("customWidget.status.private");
    else if (widget.creatorName && widget.creatorId !== currentUser.id)
      return t("customWidget.status.sharedBy", { name: widget.creatorName });
    else if (widget.creatorName && widget.creatorId === currentUser.id)
      return t("customWidget.status.sharedByYou");
    else return "";
  };

  return (
    <Box ml={2} display="flex" flexDirection="column">
      {widgets.map((widget, index) => (
        <FormControlLabel
          key={index}
          control={
            <Checkbox
              color="primary"
              checked={widget.added}
              onChange={onWidgetSelectedChange(widget.key)}
              value={widget.name}
            />
          }
          label={
            <Box
              flexDirection={{ xs: "column", sm: "row" }}
              display="flex"
              justifyContent="space-between"
            >
              <Typography variant="body2" color="textSecondary">
                {widget.name}
              </Typography>
              <div className={classes.divider} />
              <Typography variant="caption" color="primary">
                {widgetSubtitle(widget)}
              </Typography>
            </Box>
          }
        />
      ))}
    </Box>
  );
};

const AddCustomWidgetTab = ({
  customWidgets,
  onWidgetSelectedChange,
  onAddCustomWidgetClick,
  currentUser
}) => {
  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start">
      <Box ml={-1} mb={1.5}>
        <Button
          startIcon={<Add />}
          color="primary"
          onClick={onAddCustomWidgetClick}
        >
          Create a Custom HTML Widget
        </Button>
      </Box>
      <WidgetsCheckboxList
        widgets={customWidgets}
        currentUser={currentUser}
        onWidgetSelectedChange={onWidgetSelectedChange}
      />
    </Box>
  );
};

const AddWidgetModal = ({
  open,
  onClose,
  onSaveAddedWidgets,
  onSaveCustomWidget,
  widgets,
  currentUser,
  btnDisabled
}) => {
  const [selectedWidgets, setSelectedWidgets] = React.useState(widgets);
  const [filesList, setFilesList] = React.useState([]);

  useEffect(() => setSelectedWidgets(widgets), [
    widgets.map(widget => widget.id).join(",")
  ]);

  const [
    createCustomWidgetHtmlView,
    setCreateCustomWidgetHtmlView
  ] = React.useState(false);
  const [customWidgetData, setCustomWidgetData] = React.useState(null);
  const [
    customWidgetFormHasError,
    setCustomWidgetFormHasError
  ] = React.useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  const classes = useStyles();

  React.useEffect(() => {
    if (widgets !== selectedWidgets) {
      setSelectedWidgets(widgets);
    }
  }, [widgets]);
  const customWidgets = selectedWidgets.filter(
    widget => widget.widgetType === "custom"
  );
  const systemWidgets = selectedWidgets.filter(
    widget => widget.widgetType === "system"
  );
  const handleWidgetSelectedChange = widgetKey => event => {
    const updatedWidgets = selectedWidgets.map(widget => {
      if (widget.key === widgetKey) {
        return {
          ...widget,
          added: event.target.checked
        };
      } else {
        return widget;
      }
    });
    setSelectedWidgets(updatedWidgets);
  };
  const handleAddCustomWidgetClick = () => {
    setCreateCustomWidgetHtmlView(true);
  };

  const handleCustomWidgetDataChange = customWidgetData => {
    setCustomWidgetData(customWidgetData);
  };

  /**
   * If the user clicks on the close icon in the modal in the craete custom HTML view, return
   * to the custom widget tab. If the user clicks on the close icon in the add widgets tabs view,
   * close the modal.
   */
  const handleCancelClick = () => {
    if (createCustomWidgetHtmlView) {
      setCreateCustomWidgetHtmlView(false);
    } else {
      onClose();
    }
  };

  const handleCloseClick = () => {
    setCurrentTab(0);
    setCreateCustomWidgetHtmlView(false);
    onClose();
  };

  const handleSaveCustomWidget = async () => {
    if (
      Object.keys(customWidgetData).some(
        widget => customWidgetData[widget].error
      )
    ) {
      setCustomWidgetFormHasError(true);
    } else {
      const tmpObj = await filesPreloading(
        filesList,
        customWidgetData.content.value
      );

      const newWidgetData = {
        name: customWidgetData.name.value,
        description: customWidgetData.description.value,
        layout: customWidgetDefaultLayout,
        added: true,
        isPrivate: customWidgetData.isPrivate.value,
        content: tmpObj.content,
        images: tmpObj.images,
        media: tmpObj.media,
        attachments: tmpObj.attachments
      };

      onSaveCustomWidget(newWidgetData);
      setCreateCustomWidgetHtmlView(false);
    }
  };

  function getDialogContent() {
    return createCustomWidgetHtmlView ? (
      <CreateCustomWidget
        onChange={handleCustomWidgetDataChange}
        formSaveAttempted={customWidgetFormHasError}
        onFileAdd={(fileObj, tempLink, type) => {
          let list = filesList;
          list.push({ fileObj, tempLink, type });
          setFilesList(list);
        }}
      />
    ) : (
      <React.Fragment>
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={currentTab}
          onChange={(ev, newTabIndex) => setCurrentTab(newTabIndex)}
          className={classes.tabs}
          variant={"fullWidth"}
        >
          <Tab label={WidgetsModalTabs[0].label} />
          <Tab label={WidgetsModalTabs[1].label} />
        </Tabs>
        <SwipeableViews
          axis={"x"}
          index={currentTab}
          onChangeIndex={newTabIndex => setCurrentTab(newTabIndex)}
        >
          <TabContainer>
            {currentTab === WidgetsModalTabs[0].key && (
              <WidgetsCheckboxList
                widgets={systemWidgets}
                onWidgetSelectedChange={handleWidgetSelectedChange}
              />
            )}
          </TabContainer>
          <TabContainer>
            {currentTab === WidgetsModalTabs[1].key && (
              <AddCustomWidgetTab
                customWidgets={customWidgets}
                currentUser={currentUser}
                onWidgetSelectedChange={handleWidgetSelectedChange}
                onAddCustomWidgetClick={handleAddCustomWidgetClick}
              />
            )}
          </TabContainer>
        </SwipeableViews>
      </React.Fragment>
    );
  }

  function getDialogActions() {
    return (
      <React.Fragment>
        <Button
          onClick={handleCancelClick}
          color="primary"
          disabled={btnDisabled}
        >
          Cancel
        </Button>
        {createCustomWidgetHtmlView ? (
          <Button
            onClick={handleSaveCustomWidget}
            className={classes.saveButton}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        ) : (
          <Button
            onClick={() => {
              onSaveAddedWidgets(selectedWidgets);
            }}
            className={classes.saveButton}
            color="primary"
            variant="contained"
            disabled={btnDisabled}
          >
            Add Widget
          </Button>
        )}
      </React.Fragment>
    );
  }
  return (
    <Modal
      disableEnforceFocus
      showCloseIcon
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {createCustomWidgetHtmlView
            ? "Create Custom HTML Widget"
            : "Add Widget"}
        </Typography>
      }
      open={open}
      onClose={handleCloseClick}
      dialogContent={getDialogContent()}
      dialogActions={getDialogActions()}
      maxWidth="sm"
      fullWidth
      scrollable
      padding="none"
    />
  );
};

AddWidgetModal.propTypes = {
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool.isRequired,
  /**
   * Called when the modal is closed
   */
  onClose: PropTypes.func,
  /**
   * Called when the user clicks the add widgets button to save their newly selected added widgets
   */
  onAddWidget: PropTypes.func,
  onSaveAddedWidgets: PropTypes.func,
  /**
   * Called when a new custom widget is saved
   */
  onSaveCustomWidget: PropTypes.func,
  /**
   * Array containing widgets to be shown in checklists in modal
   */
  widgets: PropTypes.array
};

export default AddWidgetModal;
